import type { ProviderCredential } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';

import type { Column } from '@/components/ui/data-table';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton, EmptyState, ErrorState } from '@/components/ui/state-views';
import { StatusBadge } from '@/components/ui/status-badge';
import { getProviderCredentials } from '@/config/api-helpers';
import { formatDate } from '@/utils/format';

const STATUS_STYLES: Record<NonNullable<ProviderCredential['status']>, string> = {
  ACTIVE: 'bg-green-50 text-green-700',
  IN_PROGRESS: 'bg-yellow-50 text-yellow-700',
  NOT_STARTED: 'bg-neutral-50 text-neutral-600',
};

const STATUS_LABELS: Record<NonNullable<ProviderCredential['status']>, string> = {
  ACTIVE: 'Active',
  IN_PROGRESS: 'In Progress',
  NOT_STARTED: 'Not Started',
};

const columns: Column<ProviderCredential>[] = [
  { key: 'payer', header: 'Payer', render: (c) => <span className='text-neutral-900'>{c.payerName ?? '\u2014'}</span> },
  {
    key: 'contract',
    header: 'Contract',
    render: (c) => <span className='text-neutral-700'>{c.contractLabel ?? c.candidPayerName ?? '\u2014'}</span>,
  },
  { key: 'state', header: 'State', render: (c) => <span className='text-neutral-700'>{c.state ?? '\u2014'}</span> },
  {
    key: 'status',
    header: 'Status',
    render: (c) =>
      c.status != null ? <StatusBadge status={c.status} colors={STATUS_STYLES} label={STATUS_LABELS[c.status]} /> : '—',
  },
  {
    key: 'effective',
    header: 'Effective Date',
    render: (c) => <span className='text-neutral-700'>{c.effectiveDate ? formatDate(c.effectiveDate) : '\u2014'}</span>,
  },
  {
    key: 'submitted',
    header: 'Submitted',
    render: (c) => (
      <span className='text-neutral-700'>
        {c.applicationSubmissionDate ? formatDate(c.applicationSubmissionDate) : '\u2014'}
      </span>
    ),
  },
  {
    key: 'completed',
    header: 'Completed',
    render: (c) => (
      <span className='text-neutral-700'>
        {c.credentiallingCompleteDate ? formatDate(c.credentiallingCompleteDate) : '\u2014'}
      </span>
    ),
  },
];

export function CredentialingTab({ providerId }: { providerId: number }) {
  const {
    data: credentials,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['provider-credentials', providerId],
    queryFn: () => getProviderCredentials(providerId),
  });

  if (error)
    return <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Credentials are unavailable' />;
  if (isLoading) return <DataTableSkeleton columns={columns.length} label='Loading credentials' />;
  if (!credentials || credentials.length === 0) return <EmptyState message='No credentialing records found.' />;

  return <DataTable columns={columns} data={credentials} keyFn={(c) => c.id} />;
}
